import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getImpactEvulations, getUserHistory } from 'services/api';
import { EvaluationType, EnrollmentStatus } from 'types/evaluation';

interface ImpactEvaluationsData {
  evaluations: any[];
  history: any[];
  hasEvaluations: boolean;
  loading: boolean;
  error: string | null;
  impactError: string | null;
  historyError: string | null;
  refetch: () => void;
}

interface ImpactEvaluationsContextType {
}

const ImpactEvaluationsContext = createContext<ImpactEvaluationsContextType | undefined>(undefined);

interface ImpactEvaluationsProviderProps {
  children: ReactNode;
}

export const ImpactEvaluationsProvider: React.FC<ImpactEvaluationsProviderProps> = ({ children }) => {
  const contextValue: ImpactEvaluationsContextType = {};

  return (
    <ImpactEvaluationsContext.Provider value={contextValue}>
      {children}
    </ImpactEvaluationsContext.Provider>
  );
};

export const useImpactEvaluationsQuery = (userId: number, evaluationType: EvaluationType): ImpactEvaluationsData => {
  const evaluationsQuery = useQuery(
    ['impactEvaluations', userId, evaluationType],
    () => getImpactEvulations(userId, { evaluationType, onlyPending: false }),
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000, 
      cacheTime: 10 * 60 * 1000, 
      retry: 2,
      onError: (error) => {
        console.error('Erro ao carregar avaliações de impacto:', error);
      }
    }
  );

  const historyQuery = useQuery(
    ['userHistory', userId],
    () => getUserHistory(userId),
    {
      enabled: !!userId,
      retry: 2,
      onError: (error) => {
        console.error('Erro ao carregar histórico do usuário:', error);
      }
    }
  );

  const loading = evaluationsQuery.isLoading || historyQuery.isLoading;
  const impactError = evaluationsQuery.error ? 'Erro ao carregar avaliações de impacto' : null;
  const historyError = historyQuery.error ? 'Erro ao carregar histórico' : null;
  const error = impactError || historyError;

  let evaluations: any[] = [];
  let history: any[] = [];

  if (evaluationsQuery.data && historyQuery.data) {
    const rawEvaluations = evaluationsQuery.data.data || [];
    const userHistory = historyQuery.data.data?.history || historyQuery.data.data || [];
    
    history = userHistory;
    
    evaluations = rawEvaluations.filter((evaluation: any) => {
      const enrollment = userHistory.find((historyItem: any) => 
        historyItem.idEnrollment === evaluation.idEnrollment
      );
      
      if (!enrollment) return false;
      
      const enrollmentStatus = enrollment.enrollmentStatus;
      return enrollmentStatus === EnrollmentStatus.COMPLETED || 
             enrollmentStatus === EnrollmentStatus.PASSED;
    });
  }

  const refetch = () => {
    evaluationsQuery.refetch();
    historyQuery.refetch();
  };

  return {
    evaluations,
    history,
    hasEvaluations: evaluations.length > 0,
    loading,
    error,
    impactError,
    historyError,
    refetch,
  };
};

export const useImpactEvaluationsContext = (): ImpactEvaluationsContextType => {
  const context = useContext(ImpactEvaluationsContext);
  if (context === undefined) {
    throw new Error('useImpactEvaluationsContext must be used within an ImpactEvaluationsProvider');
  }
  return context;
};

export const useStudentImpactEvaluations = (userId: number): ImpactEvaluationsData => {
  return useImpactEvaluationsQuery(userId, EvaluationType.STUDENT_IMPACT);
};

export const useManagerImpactEvaluations = (userId: number): ImpactEvaluationsData => {
  return useImpactEvaluationsQuery(userId, EvaluationType.IMPACT);
};

export default ImpactEvaluationsContext;
